import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FormHelperText from "@mui/material/FormHelperText";
import { useForm } from "react-hook-form";
import { frenchNameRegex, emailRegex } from "@shared/utils/commonUtils";
import useEnvironment from "@shared/hooks/useEnvironment";
import ReCAPTCHA from "react-google-recaptcha";
import Icon from "@shared/components/Icon";
import ContactService from "@project/services/ContactService";
import CaptchaService from "@project/services/CaptchaService";
import useCookieStore from "@shared/store/CookieStore";
import ContactTextField from "./ContactTextField";
import ContactFormAttachments from "./ContactFormAttachments";
import ContactFormConsent from "./ContactFormConsent";
import Stack from "@mui/material/Stack";
const loaderStyled = {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    bgcolor: "#eff4fd",
    opacity: "0.5",
    zIndex: 1000,
    "& svg": {
        color: "secondary.main",
    },
};
const formValidatedStyled = {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    px: 0,
    py: 10,
};
export const recaptchaStyled = {
    "& .grecaptcha-badge": {
        bottom: { xs: "123px !important", lg: 0 },
        zIndex: "525",
    },
};
const defaultFormValues = {
    name: "",
    firstName: "",
    email: "",
    subject: "",
    message: "",
    consent: false,
};
const ContactForm = (props) => {
    const { id, recipient, ...others } = props;
    const { recaptcha_site_key_invisible, mail_attachment_size = 10 * 1024 * 1024, // 10MB
    mail_allowed_attachment_type, } = useEnvironment();
    const [loader, setLoader] = useState(false);
    const [formSended, setFormSended] = useState(false);
    const [sendError, setSendError] = useState(null);
    const [filesForm, setFilesForm] = useState(null);
    const recaptchaRef = useRef(null);
    const pendingFormValuesRef = useRef(null);
    const { control, handleSubmit, reset, formState: { errors, isSubmitted, isValid }, } = useForm({
        mode: "onChange",
        defaultValues: defaultFormValues,
    });
    const { googleRecaptchaConsent } = useCookieStore((state) => ({
        googleRecaptchaConsent: state.googleRecaptchaConsent,
    }));
    const filesSize = (Array.isArray(filesForm) &&
        filesForm.reduce((totalSize, fileForm) => totalSize + (fileForm?.size || 0), 0)) ||
        0;
    const fileFormError = Boolean(filesSize > mail_attachment_size);
    const showError = (key) => {
        if (!isSubmitted) {
            return false;
        }
        if (key === "fileForm") {
            return fileFormError;
        }
        return Boolean(errors[key]);
    };
    const isFormValid = isValid && !fileFormError;
    const sendMail = async (values) => {
        const formData = new FormData();
        Array.isArray(filesForm) &&
            filesForm.forEach((fileForm) => {
                formData.append("attachment", fileForm, fileForm.name);
            });
        formData.append("recipient", recipient || "");
        formData.append("name", values.name);
        formData.append("firstName", values.firstName);
        formData.append("email", values.email);
        formData.append("subject", values.subject);
        formData.append("message", decodeURIComponent(encodeURIComponent(values.message).replace(/%0A/g, "<br/>")));
        try {
            const response = await ContactService.postMail(formData);
            if (response &&
                response.status &&
                ![200, 201, 202].includes(response.status)) {
                setSendError(response.message);
                return;
            }
            setFormSended(true);
            reset(defaultFormValues);
            setFilesForm(null);
        }
        finally {
            setLoader(false);
        }
    };
    const checkUser = async (value) => {
        const recaptchaValidated = await CaptchaService.verifyCaptcha(value);
        if (recaptchaValidated.success && pendingFormValuesRef.current) {
            await sendMail(pendingFormValuesRef.current);
            pendingFormValuesRef.current = null;
        }
        else {
            setLoader(false);
        }
    };
    const handleValidate = (values) => {
        if (!isFormValid) {
            return;
        }
        setLoader(true);
        setSendError(null);
        if (!recaptcha_site_key_invisible) {
            sendMail(values);
        }
        else {
            pendingFormValuesRef.current = values;
            recaptchaRef.current?.execute();
        }
    };
    const handleUploadFile = (e) => {
        const fileToUpload = e.target.files[0];
        if (!fileToUpload) {
            return;
        }
        setFilesForm(filesForm ? [...filesForm, fileToUpload] : [fileToUpload]);
        e.target.value = null;
    };
    const handleRemoveFile = (fileFormName) => {
        setFilesForm(filesForm && filesForm.length > 1
            ? filesForm.filter((fileForm) => fileForm.name !== fileFormName)
            : null);
    };
    const handleBackToForm = () => {
        setFormSended(false);
        setSendError(null);
        reset(defaultFormValues);
    };
    if (formSended) {
        return (_jsx(Box, { ...others, children: _jsxs(Box, { sx: formValidatedStyled, role: "status", "aria-live": "polite", children: [_jsx(Box, { pb: 3, children: _jsx(Icon, { icon: "check-circle", type: "fas", title: "Message envoy\u00E9", style: { fontSize: "48px", color: "#24821a" }, "aria-hidden": "false" }) }), _jsx(Box, { pb: [1.5], children: _jsx(Typography, { variant: "h5", component: "p", sx: { fontSize: "16px", fontWeight: "600" }, children: "Votre message a bien \u00E9t\u00E9 envoy\u00E9" }) }), _jsx(Button, { variant: "outlined", onClick: handleBackToForm, sx: { mt: 2 }, "aria-label": "Revenir au formulaire de contact", children: "Retour au formulaire" })] }) }, id));
    }
    return (_jsxs(Box, { ...others, children: [_jsx(Typography, { variant: "caption", component: "p", sx: { mb: 5 }, children: "* Les champs marqu\u00E9s d\u2019un ast\u00E9risque sont obligatoires." }), _jsxs(Stack, { spacing: 5, component: "form", action: "/", method: "POST", onSubmit: handleSubmit(handleValidate), noValidate: true, role: "form", "aria-label": "Formulaire de contact", children: [loader && (_jsx(Grid, { container: true, justifyContent: "center", alignItems: "center", sx: loaderStyled, children: _jsx(CircularProgress, {}) })), _jsxs(Stack, { direction: { xs: "column", lg: "row" }, spacing: 5, sx: {
                            alignItems: "flex-start",
                        }, children: [_jsx(ContactTextField, { name: "name", label: "Nom *", control: control, rules: {
                                    required: "Votre nom est requis",
                                    pattern: {
                                        value: frenchNameRegex,
                                        message: "Votre nom n'est pas valide",
                                    },
                                }, showError: showError, errors: errors, onResetError: () => setSendError(null), autocomplete: "family-name" }), _jsx(ContactTextField, { name: "firstName", label: "Pr\u00E9nom *", control: control, rules: {
                                    required: "Votre prénom est requis",
                                    pattern: {
                                        value: frenchNameRegex,
                                        message: "Votre prénom n'est pas valide",
                                    },
                                }, showError: showError, errors: errors, onResetError: () => setSendError(null), autocomplete: "given-name" })] }), _jsx(ContactTextField, { name: "email", label: "E-mail *", control: control, rules: {
                            required: "Votre adresse e-mail est requise",
                            pattern: {
                                value: emailRegex,
                                message: "Le format de l'adresse e-mail n'est pas correct",
                            },
                        }, showError: showError, errors: errors, onResetError: () => setSendError(null), type: "email", autocomplete: "email", exampleText: "Exemple : m-dupond@gmail.com" }), _jsx(ContactTextField, { name: "subject", label: "Sujet *", control: control, rules: { required: "Le sujet est requis" }, showError: showError, errors: errors, onResetError: () => setSendError(null) }), _jsx(ContactTextField, { name: "message", label: "Message *", control: control, rules: { required: "Le message est requis" }, showError: showError, errors: errors, onResetError: () => setSendError(null), multiline: true, rows: 10 }), _jsx(ContactFormAttachments, { mailAllowedAttachmentType: mail_allowed_attachment_type, filesForm: filesForm, showError: showError, onUploadFile: handleUploadFile, onRemoveFile: handleRemoveFile }), _jsx(ContactFormConsent, { control: control, showError: showError, onResetError: () => setSendError(null) }), recaptcha_site_key_invisible && googleRecaptchaConsent && (_jsx(Box, { sx: recaptchaStyled, role: "img", "aria-label": "V\u00E9rification reCAPTCHA en cours", children: _jsx(ReCAPTCHA, { ref: recaptchaRef, size: "invisible", sitekey: recaptcha_site_key_invisible, onChange: (recaptchaValue) => {
                                checkUser(recaptchaValue);
                            }, "aria-label": "V\u00E9rification reCAPTCHA invisible" }) })), _jsx(Box, { display: "flex", flexDirection: "row-reverse", children: _jsx(Tooltip, { title: recaptcha_site_key_invisible && !googleRecaptchaConsent
                                ? 'Vous devez accepter les cookies "Google reCAPTCHA" pour utiliser cette fonctionnalité'
                                : "", children: _jsx(Box, { children: _jsx(Button, { disabled: !!recaptcha_site_key_invisible && !googleRecaptchaConsent, variant: "contained", color: "secondary", type: "submit", "aria-label": `${!isFormValid ? "Formulaire incomplet. " : ""}Envoyer le formulaire de contact`, children: "Envoyer" }) }) }) }), sendError && (_jsxs(FormHelperText, { error: true, sx: { fontSize: "1rem" }, role: "alert", children: [_jsx(Icon, { icon: "exclamation-circle", title: "Erreur" }), "\u00A0", sendError] }))] })] }, id));
};
export default ContactForm;
